import React, { useState } from 'react';
import { Calendar, Filter, RotateCcw } from 'lucide-react';

const DATE_PRESETS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom Range' },
];

const GROUP_BY_OPTIONS = [
  { value: 'stage', label: 'Stage' },
  { value: 'owner', label: 'Owner' },
  { value: 'account', label: 'Account' },
  { value: 'rep_firm', label: 'Rep Firm' },
  { value: 'lead_source', label: 'Lead Source' },
  { value: 'industry', label: 'Industry' },
];

const REVENUE_GROUP_BY_OPTIONS = GROUP_BY_OPTIONS.filter(o => o.value !== 'stage');

export default function ReportFilters({
  reportType,
  dateRange,
  onDateRangeChange,
  groupBy,
  onGroupByChange,
  filters,
  onFiltersChange,
  filterOptions,
  onReset,
  hideGroupBy,
}) {
  const [showFilters, setShowFilters] = useState(false);
  const groupByOptions = reportType === 'revenue' ? REVENUE_GROUP_BY_OPTIONS : GROUP_BY_OPTIONS;

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v.length > 0).length;

  return (
    <div className="report-filters">
      <div className="report-filters-row">
        <div className="report-filters-group">
          <label className="report-filter-label">
            <Calendar size={14} />
            Date Range
          </label>
          <select
            className="report-filter-select"
            value={dateRange.preset}
            onChange={(e) => onDateRangeChange({ ...dateRange, preset: e.target.value })}
          >
            {DATE_PRESETS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        {dateRange.preset === 'custom' && (
          <>
            <div className="report-filters-group">
              <label className="report-filter-label">Start</label>
              <input
                type="date"
                className="report-filter-input"
                value={dateRange.startDate || ''}
                onChange={(e) => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
              />
            </div>
            <div className="report-filters-group">
              <label className="report-filter-label">End</label>
              <input
                type="date"
                className="report-filter-input"
                value={dateRange.endDate || ''}
                onChange={(e) => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
              />
            </div>
          </>
        )}

        {!hideGroupBy && <div className="report-filters-group">
          <label className="report-filter-label">Group By</label>
          <select
            className="report-filter-select"
            value={groupBy}
            onChange={(e) => onGroupByChange(e.target.value)}
          >
            {groupByOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>}

        <button
          className={`btn btn-secondary report-filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={14} />
          Filters
          {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
        </button>

        <button className="btn btn-ghost report-filter-reset" onClick={onReset} title="Reset all filters">
          <RotateCcw size={14} />
        </button>
      </div>

      {showFilters && filterOptions && (
        <div className="report-filters-expanded">
          <div className="report-filters-grid">
            <div className="report-filters-group">
              <label className="report-filter-label">Owner</label>
              <select
                className="report-filter-select"
                value={filters.owner_id || ''}
                onChange={(e) => handleFilterChange('owner_id', e.target.value)}
              >
                <option value="">All Owners</option>
                {filterOptions.owners?.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            <div className="report-filters-group">
              <label className="report-filter-label">Account</label>
              <select
                className="report-filter-select"
                value={filters.account_id || ''}
                onChange={(e) => handleFilterChange('account_id', e.target.value)}
              >
                <option value="">All Accounts</option>
                {filterOptions.accounts?.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            <div className="report-filters-group">
              <label className="report-filter-label">Rep Firm</label>
              <select
                className="report-filter-select"
                value={filters.rep_firm_id || ''}
                onChange={(e) => handleFilterChange('rep_firm_id', e.target.value)}
              >
                <option value="">All Rep Firms</option>
                {filterOptions.repFirms?.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>

            <div className="report-filters-group">
              <label className="report-filter-label">Lead Source</label>
              <select
                className="report-filter-select"
                value={filters.lead_source_id || ''}
                onChange={(e) => handleFilterChange('lead_source_id', e.target.value)}
              >
                <option value="">All Sources</option>
                {filterOptions.leadSources?.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="report-filters-group">
              <label className="report-filter-label">Industry</label>
              <select
                className="report-filter-select"
                value={filters.industry || ''}
                onChange={(e) => handleFilterChange('industry', e.target.value)}
              >
                <option value="">All Industries</option>
                {filterOptions.industries?.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>

            {reportType !== 'revenue' && (
              <div className="report-filters-group">
                <label className="report-filter-label">Stage</label>
                <select
                  className="report-filter-select"
                  value={filters.stage_id || ''}
                  onChange={(e) => handleFilterChange('stage_id', e.target.value)}
                >
                  <option value="">All Stages</option>
                  {filterOptions.stages?.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
